/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Tearsheet } from '@carbon/ibm-products';
import {
    Button, Loading, Tooltip,
} from '@carbon/react';
import { Text } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { ChevronDown, Information } from '@carbon/icons-react';

import sharedMessages from 'Components/utils/sharedMessages';
import { useEntityContext } from 'Screens/Dashboard/AllEntities/EntityProvider/EntityContext';
import GeneralDetails, { EntityDetailsMode } from 'Screens/Dashboard/AllEntities/Details/GeneralDetails';
import { useCreateEntityMutation, useGetEntitiesQuery } from 'api/entityEndpoints';
import { EMAIL_PATTERN } from 'utils/constants';
import useGetUser from 'hooks/useGetUser';
import { UserRole, UserShortEntityDto } from 'api/dtos/user';
import useGetCurrentSubscription from 'Screens/Dashboard/Subscription/useGetCurrentSubscription';

import styles from './AddEntity.module.scss';
import AddEntityInfoCards from './AddEntityInfoCards/AddEntityInfoCards';

type Props = {
    open: boolean;
    onClose: () => void;
    isParishCreation?: boolean;
}

type ParishOption = {
    id: string;
    name: string;
    role: UserRole;
    canCreateGroups?: boolean;
}

const AddEntity = ({
    open,
    onClose,
    isParishCreation = false,
}: Props) => {
    const intl = useIntl();
    const user = useGetUser();
    const { state, dispatch } = useEntityContext();
    const { isPaidPlan } = useGetCurrentSubscription();
    const [isCreatingParishFromGroupModal, setIsCreatingParishFromGroupModal] = useState(false);
    const [isParishSelectOpen, setIsParishSelectOpen] = useState(false);
    const { data: entities } = useGetEntitiesQuery(undefined, { skip: isParishCreation });

    const [submitEntity, { isLoading }] = useCreateEntityMutation();

    const parishOptions = useMemo<ParishOption[]>(() => {
        const optionMap = new Map<string, ParishOption>();

        const addEntityOption = (entity: UserShortEntityDto) => {
            if (entity.isParish || (!entity.parentId && !entity.isIndependent)) {
                optionMap.set(entity.id, {
                    id: entity.id,
                    name: entity.name,
                    role: entity.role,
                    canCreateGroups: entity.canCreateGroups,
                });
            }

            if (entity.parentId && entity.parentName && !optionMap.has(entity.parentId)) {
                optionMap.set(entity.parentId, {
                    id: entity.parentId,
                    name: entity.parentName,
                    role: entity.role,
                });
            }
        };

        (user?.entities ?? []).forEach(addEntityOption);
        (entities ?? []).forEach(addEntityOption);

        return [...optionMap.values()];
    }, [entities, user]);
    const parishId = useMemo(
        () => parishOptions[0]?.id ?? null,
        [parishOptions],
    );
    const selectedParish = useMemo(
        () => parishOptions.find((entity) => entity.id === state.parentId) ?? null,
        [parishOptions, state.parentId],
    );
    const hasOwnedParish = useMemo(
        () => parishOptions.some((entity) => entity.role === UserRole.Owner),
        [parishOptions],
    );
    const isCreateParishDisabled = hasOwnedParish || !isPaidPlan;
    const createParishTooltipMessage = hasOwnedParish
        ? messages.createParishOwnerTooltip
        : messages.createParishTooltip;
    const isCreatingParish = isParishCreation || isCreatingParishFromGroupModal;
    const detailsMode: EntityDetailsMode = useMemo(() => {
        if (isCreatingParish) {
            return 'parish';
        }

        return state.isIndependent ? 'independent' : 'parishGroup';
    }, [isCreatingParish, state.isIndependent]);
    const isSelectedParishGroupLimitReached = detailsMode === 'parishGroup'
        && selectedParish?.canCreateGroups === false;
    const showContactFields = detailsMode === 'parish';
    const isEmailValid = useMemo(
        () => EMAIL_PATTERN.test(state.email) || state.email.length === 0,
        [state.email],
    );
    const isSubmitDisabled = state.name.trim().length === 0
        || (showContactFields && !isEmailValid && state.email.length > 0)
        || (detailsMode === 'parishGroup' && (!state.parentId || isSelectedParishGroupLimitReached))
        || (!isParishCreation && isCreatingParish && !isPaidPlan);

    useEffect(() => {
        if (detailsMode !== 'parishGroup' || state.parentId || !parishId) {
            return;
        }

        dispatch({ type: 'SET_PARENT_ID', payload: parishId });
    }, [detailsMode, dispatch, parishId, state.parentId]);

    const switchToIndependentGroup = () => {
        dispatch({ type: 'SET_IS_INDEPENDENT', payload: true });
        dispatch({ type: 'SET_IS_SUBGROUP', payload: false });
        dispatch({ type: 'SET_PARENT_ID', payload: null });
    };

    const switchToParishGroup = () => {
        dispatch({ type: 'SET_IS_INDEPENDENT', payload: false });
        dispatch({ type: 'SET_IS_SUBGROUP', payload: true });
        dispatch({ type: 'SET_PARENT_ID', payload: parishId });
    };

    const switchToParishCreation = () => {
        if (isCreateParishDisabled) {
            return;
        }

        setIsCreatingParishFromGroupModal(true);
        dispatch({ type: 'SET_IS_INDEPENDENT', payload: false });
        dispatch({ type: 'SET_IS_SUBGROUP', payload: false });
        dispatch({ type: 'SET_PARENT_ID', payload: null });
    };

    const switchToGroupCreation = () => {
        setIsCreatingParishFromGroupModal(false);
        dispatch({ type: 'SET_IS_INDEPENDENT', payload: false });
        dispatch({ type: 'SET_IS_SUBGROUP', payload: Boolean(parishId) });
        dispatch({ type: 'SET_PARENT_ID', payload: parishId });
    };

    const onParishChange = (selectedItem: ParishOption) => {
        dispatch({ type: 'SET_PARENT_ID', payload: selectedItem.id });
        setIsParishSelectOpen(false);
    };

    const handleFormSubmit = async () => {
        if (isSubmitDisabled) {
            return;
        }

        try {
            await submitEntity({
                name: state.name.trim(),
                phoneNumber: showContactFields ? state.phoneNumber : '',
                email: showContactFields ? state.email : '',
                website: showContactFields ? state.website : '',
                isEnabled: true,
                tags: detailsMode === 'parishGroup' ? [] : state.tags.filter((tag) => !tag.isRequired).map((tag) => ({
                    name: tag.name,
                })),
                parentId: detailsMode === 'parishGroup' ? state.parentId : null,
                isIndependent: detailsMode === 'independent',
                usesPoints: state.usesPoints,
            }).unwrap();

            onClose();
        } catch {
            // RTK Query handles the API error notification.
        }
    };

    return (
        <>
            {isLoading && <Loading />}
            <Tearsheet
                open={open}
                title={intl.formatMessage(isCreatingParish ? messages.addParish : messages.addGroup)}
                description={intl.formatMessage(isCreatingParish ? messages.parishDescription : messages.description)}
                closeIconDescription={intl.formatMessage(sharedMessages.close)}
                hasCloseIcon
                onClose={onClose}
                selectorPrimaryFocus="#name"
                actions={[
                    {
                        label: intl.formatMessage(sharedMessages.cancel),
                        kind: 'secondary',
                        onClick: onClose,
                    },
                    {
                        label: intl.formatMessage(sharedMessages.submit),
                        kind: 'primary',
                        disabled: isLoading || isSubmitDisabled,
                        loading: isLoading,
                        onClick: handleFormSubmit,
                    },
                ]}
            >
                <div className={styles.form}>
                    <div className={styles.formFields}>
                        {!isParishCreation && (
                            <div className={styles.modeActions}>
                                {isCreatingParish ? (
                                    <Button kind="ghost" size="sm" onClick={switchToGroupCreation}>
                                        {intl.formatMessage(messages.createGroup)}
                                    </Button>
                                ) : (
                                    <>
                                        {state.isIndependent ? (
                                            <Button kind="ghost" size="sm" onClick={switchToParishGroup}>
                                                {intl.formatMessage(messages.createParishGroup)}
                                            </Button>
                                        ) : (
                                            <Button kind="ghost" size="sm" onClick={switchToIndependentGroup}>
                                                {intl.formatMessage(messages.createIndependentGroup)}
                                            </Button>
                                        )}
                                        <Tooltip
                                            label={intl.formatMessage(createParishTooltipMessage)}
                                            align="bottom"
                                        >
                                            <div className={styles.parishCreationAction}>
                                                <Button
                                                    kind="ghost"
                                                    size="sm"
                                                    disabled={isCreateParishDisabled}
                                                    onClick={switchToParishCreation}
                                                >
                                                    {intl.formatMessage(messages.createParish)}
                                                </Button>
                                                <Information size={16} />
                                            </div>
                                        </Tooltip>
                                    </>
                                )}
                            </div>
                        )}
                        <GeneralDetails
                            withStep={false}
                            mode={detailsMode}
                            showUsesPoints
                        />
                        {detailsMode === 'parishGroup' && (
                            <div className={styles.parishSelectContainer}>
                                <label className={styles.parishSelectLabel} htmlFor="entity-parent-parish-select">
                                    {intl.formatMessage(messages.parentParishLabel)}
                                </label>
                                <div className={styles.customSelect}>
                                    <button
                                        id="entity-parent-parish-select"
                                        type="button"
                                        className={styles.customSelectButton}
                                        onClick={() => {
                                            setIsParishSelectOpen((isOpen) => !isOpen);
                                        }}
                                    >
                                        <span>
                                            {selectedParish?.name ?? intl.formatMessage(messages.parentParishPlaceholder)}
                                        </span>
                                        <ChevronDown size={18} />
                                    </button>
                                    {isParishSelectOpen && (
                                        <div className={styles.customSelectMenu}>
                                            {parishOptions.map((parish) => (
                                                <button
                                                    key={parish.id}
                                                    type="button"
                                                    className={styles.customSelectOption}
                                                    onClick={() => {
                                                        onParishChange(parish);
                                                    }}
                                                >
                                                    {parish.name}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {isSelectedParishGroupLimitReached && (
                                    <Text size="sm" className={styles.parishSelectError}>
                                        {intl.formatMessage(messages.parishGroupLimitError)}
                                    </Text>
                                )}
                                <Text size="sm" className={styles.parishSelectHint}>
                                    {intl.formatMessage(messages.parentParishHint)}
                                </Text>
                            </div>
                        )}
                    </div>
                    <AddEntityInfoCards className={styles.infoCards} />
                    {isLoading && <Loading />}
                </div>
            </Tearsheet>
        </>

    );
};

const messages = defineMessages({
    addGroup: {
        id: 'src.Screens.Dashboard.AddEntity.AddEntity.addGroup',
        defaultMessage: 'Dodaj grupę',
    },
    addParish: {
        id: 'src.Screens.Dashboard.AddEntity.AddEntity.addParish',
        defaultMessage: 'Dodaj parafię',
    },
    description: {
        id: 'src.Screens.Dashboard.AddEntity.AddEntity.description',
        defaultMessage: 'Wprowadź podstawowe informacje o grupie. Zajmie to mniej niż minutę.',
    },
    parishDescription: {
        id: 'src.Screens.Dashboard.AddEntity.AddEntity.parishDescription',
        defaultMessage: 'Uzupełnij dane parafii, aby odblokować panel.',
    },
    createIndependentGroup: {
        id: 'src.Screens.Dashboard.AddEntity.AddEntity.createIndependentGroup',
        defaultMessage: 'Utwórz grupę niezależną od parafii',
    },
    createParishGroup: {
        id: 'src.Screens.Dashboard.AddEntity.AddEntity.createParishGroup',
        defaultMessage: 'Utwórz grupę należącą do parafii',
    },
    createParish: {
        id: 'src.Screens.Dashboard.AddEntity.AddEntity.createParish',
        defaultMessage: 'Utwórz parafię',
    },
    createGroup: {
        id: 'src.Screens.Dashboard.AddEntity.AddEntity.createGroup',
        defaultMessage: 'Utwórz grupę zamiast parafii',
    },
    createParishTooltip: {
        id: 'src.Screens.Dashboard.AddEntity.AddEntity.createParishTooltip',
        defaultMessage: 'Jeżeli nie jesteś właścicielem żadnej parafii, mając aktywną subskrypcję możesz utworzyć własną parafię i zarządzać jej grupami.',
    },
    createParishOwnerTooltip: {
        id: 'src.Screens.Dashboard.AddEntity.AddEntity.createParishOwnerTooltip',
        defaultMessage: 'Możesz być właścicielem tylko jednej parafii. Aby utworzyć nową, najpierw przekaż własność aktualnej parafii innej osobie.',
    },
    parentParishLabel: {
        id: 'src.Screens.Dashboard.AddEntity.AddEntity.parentParishLabel',
        defaultMessage: 'Parafia',
    },
    parentParishPlaceholder: {
        id: 'src.Screens.Dashboard.AddEntity.AddEntity.parentParishPlaceholder',
        defaultMessage: 'Wybierz parafię',
    },
    parentParishHint: {
        id: 'src.Screens.Dashboard.AddEntity.AddEntity.parentParishHint',
        defaultMessage: 'Grupa zostanie utworzona w wybranej parafii.',
    },
    parishGroupLimitError: {
        id: 'src.Screens.Dashboard.AddEntity.AddEntity.parishGroupLimitError',
        defaultMessage: 'W tej parafii nie można utworzyć kolejnej grupy. Wybierz inną parafię albo sprawdź subskrypcję.',
    },
});

export default AddEntity;
